import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { format } from 'date-fns'
import { useSnackbar } from 'notistack'
import { Box, Button, Modal, Stack, Typography } from '@mui/material'
import { formatNumberAsPrice } from '@shared/common'
import { i18WithParams as t } from '@shared/locales'
import { useAdminCancelInvoice, useClientCancelInvoice } from 'service-api/src/hooks'
import { getRefundRestrictions } from './helper'
import { getInvoiceDates } from 'shared'
import { TextInputMoney } from 'textinput_money'
import { Types } from 'service-api'
import { ButtonActions } from 'button_actions'

type Props = {
  invoice: Types.CustomInvoiceAdminDto
  order: Types.CustomOrderAdminDto
  canOverrideRefund?: boolean
  open: boolean
  onClose: () => void
  onCancelInvoice: () => void
}
export const CancelInvoiceModal = ({ invoice, order, canOverrideRefund, open, onClose, onCancelInvoice }: Props) => {
  const { accountName = '' } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const [refundMode, setRefundMode] = useState(false)
  const [overrideRefundAmount, setOverrideRefundAmount] = useState('')

  const { startDate: invoiceStartDate } = useMemo(() => getInvoiceDates(invoice), [invoice])
  const { refundSettingToApply, maxCancelBy } = useMemo(
    () => getRefundRestrictions(order, invoiceStartDate),
    [order, invoiceStartDate]
  )
  const refundEligibleAmount = (invoice.netPaid || 0) * parseFloat(refundSettingToApply?.rate || '0')

  const { refetch: clientCancelInvoice, isFetching: isClientCancellingInvoice } = useClientCancelInvoice(
    accountName,
    order.id!,
    invoice.id!
  )
  const { refetch: adminCancelInvoice, isFetching: isAdminCancellingInvoice } = useAdminCancelInvoice(
    accountName,
    order.id!,
    invoice.id!,
    overrideRefundAmount ? parseFloat(overrideRefundAmount) : undefined
  )
  useEffect(() => {
    if (!open) {
      setRefundMode(false)
    }
  }, [open])

  useEffect(() => {
    setOverrideRefundAmount(refundMode ? refundEligibleAmount.toFixed(2) : '')
  }, [refundMode, refundEligibleAmount])

  const renderRefundMode = () => (
    <>
      <Typography variant="h4" mb={3}>
        {t('cancelInvoice.overrideRefund.title')}
      </Typography>
      <Stack direction="row" justifyContent="space-between">
        <Stack>
          <Typography variant="body1">{t('global.label.amountPaid')}</Typography>
          <Typography variant="h6">{formatNumberAsPrice(invoice.netPaid || 0)}</Typography>
        </Stack>
        <TextInputMoney
          autoFocus={true}
          id="refund-amount"
          label={t('cancelInvoice.refundAmount')}
          value={overrideRefundAmount}
          onChange={(e) => setOverrideRefundAmount(e.target.value)}
        />
      </Stack>
    </>
  )

  const handleSubmit = async () => {
    if (accountName && order.id && invoice.id) {
      const cancelInvoice = canOverrideRefund ? adminCancelInvoice : clientCancelInvoice
      await cancelInvoice()
        .then(({ data, error }) => {
          if (data) {
            onCancelInvoice()
            enqueueSnackbar(t('cancelInvoice.success', { num: (invoice.name || '').toString() }), {
              variant: 'success',
            })
          } else {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            const errorMsg = error?.response?.data?.error ?? t('global.label.unexpectedError')
            enqueueSnackbar(errorMsg, { variant: 'warning' })
          }
        })
        .catch(() => {
          enqueueSnackbar(t('global.label.unexpectedError'), { variant: 'error' })
        })
        .finally(() => {
          onClose()
        })
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="modal-cancel-invoice"
      aria-describedby="modal-cancel-invoice-for-order"
    >
      <Box sx={styledBox}>
        {refundMode ? (
          renderRefundMode()
        ) : (
          <>
            {refundSettingToApply ? (
              <Typography variant="h4" mb={3}>
                {t('cancelInvoice.confirmCancel')}
              </Typography>
            ) : (
              <>
                <Typography variant="h4" mb={3}>
                  {t('cancelInvoice.pastWindow.title')}
                </Typography>
                {!!maxCancelBy && (
                  <Typography
                    variant="body1"
                    dangerouslySetInnerHTML={{
                      __html: t('cancelInvoice.pastWindow.description', { date: format(maxCancelBy, 'MMM d') }),
                    }}
                  />
                )}
              </>
            )}
            <Typography
              variant="body1"
              dangerouslySetInnerHTML={{
                __html: t('cancelInvoice.cancelDescription', {
                  amount: formatNumberAsPrice(refundEligibleAmount),
                }),
              }}
            />
            {canOverrideRefund && (
              <Box>
                <Button variant="text" onClick={() => setRefundMode(true)}>
                  {t('cancelInvoice.overrideRefund.button')}
                </Button>
              </Box>
            )}
          </>
        )}
        <Stack direction="row" justifyContent="flex-end" mt={2} mr={-2}>
          <ButtonActions
            actionEnabled={true}
            onActionLabel={t('global.label.continue')}
            onAction={handleSubmit}
            onCancel={async () => onClose()}
          />
        </Stack>
      </Box>
    </Modal>
  )
}

const styledBox = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  boxShadow: 24,
  width: 'min(456px, 100%)',
  borderRadius: 1,
  display: 'flex',
  flexDirection: 'column',
  outline: 0,
  pt: 2,
  pr: 3,
  pl: 3,
  pb: 1,
}
